"use client";

import { ChevronUp, ChevronDown, AlertCircle, LogOut } from "lucide-react";
import { useEffect, useState } from "react";

import { DemoStorage } from "@/lib/demo-mode/client";
import type { DemoConfig } from "@/lib/demo-mode/types";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";

export default function DemoModeBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [config, setConfig] = useState<DemoConfig | null>(null);
  const router = useRouter();

  useEffect(() => {
    // Check if demo mode is active
    const checkDemoMode = () => {
      const demoConfig = DemoStorage.getConfig();
      const wasCollapsed =
        sessionStorage.getItem("demo-banner-collapsed") === "true";
      if (demoConfig?.enabled) {
        setConfig(demoConfig);
        setIsVisible(true);
        setIsCollapsed(wasCollapsed);
      } else {
        setIsVisible(false);
      }
    };

    checkDemoMode();
  }, []);

  const handleExit = () => {
    if (
      confirm(
        "Are you sure you want to exit demo mode? This will clear all demo data."
      )
    ) {
      DemoStorage.clearAll();
      sessionStorage.removeItem("demo-banner-collapsed");
      setIsVisible(false);
      router.push("/demo-mode");
    }
  };

  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    sessionStorage.setItem("demo-banner-collapsed", String(newCollapsed));
  };

  if (!isVisible || !config) return null;

  // Collapsed state - small pill with expand and exit options
  if (isCollapsed) {
    return (
      <div
        className={cn(
          "fixed right-4 bottom-20 z-50",
          "bg-linear-to-r from-blue-600 to-purple-600",
          "rounded-full text-white shadow-lg",
          "transform transition-all duration-300 ease-in-out"
        )}
      >
        <div className="flex items-center gap-1 px-3 py-2">
          <AlertCircle className="h-4 w-4 opacity-90" />
          <span className="text-xs font-medium">Demo</span>
          <button
            onClick={handleToggleCollapse}
            className="ml-1 rounded-full p-1 transition-colors hover:bg-white/20"
            title="Expand"
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            onClick={handleExit}
            className="rounded-full p-1 transition-colors hover:bg-white/20"
            title="Exit Demo Mode"
          >
            <LogOut className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  // Expanded state - full banner
  return (
    <div
      className={cn(
        "fixed right-4 bottom-20 z-50",
        "max-w-sm bg-linear-to-r from-blue-600 to-purple-600",
        "rounded-lg text-white shadow-2xl",
        "transform transition-all duration-300 ease-in-out"
      )}
    >
      <div className="px-4 py-3">
        <div className="flex items-start">
          <div className="shrink-0">
            <AlertCircle className="h-5 w-5 text-white opacity-90" />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium">Demo Mode Active</p>
            <div className="mt-1 space-y-1 text-xs opacity-90">
              <p>
                Partner:{" "}
                <span className="font-semibold">
                  {String(config.partner || "unknown")}
                </span>
              </p>
              <p>
                Flow:{" "}
                <span className="font-semibold">
                  {String(config.flowType || "invitation")}
                </span>
              </p>
              <p>
                Outcome:{" "}
                <span className="font-semibold">
                  {String(config.outcome || "approved")}
                </span>
              </p>
              <p className="mt-2 text-[10px] opacity-75">
                All data is stored locally. No real API calls are made.
              </p>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleExit}
                className="rounded bg-white/20 px-2 py-1 text-xs transition-colors hover:bg-white/30"
              >
                Exit Demo
              </button>
              <button
                onClick={handleToggleCollapse}
                className="rounded bg-white/10 px-2 py-1 text-xs transition-colors hover:bg-white/20"
              >
                Collapse
              </button>
            </div>
          </div>
          <button
            onClick={handleToggleCollapse}
            className="ml-2 shrink-0 text-white/70 transition-colors hover:text-white"
            title="Collapse"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
